import { alpha, useTheme } from '@mui/material';
import { useMemo } from 'react';

interface DimensionIndicatorProps {
  widthValue: number;
  heightValue: number;
  /**
   * Total container height in pixels. The shape is sized to fill the
   * remaining space after subtracting the ruler area, and width is scaled
   * by the same factor to preserve aspect ratio (capped at 300px).
   *
   * All dimensions within the indicator render at a uniform scale
   * determined by the height axis, so every indicator is exactly this
   * tall regardless of the input dimensions.
   */
  height?: number;
}

const DEFAULT_HEIGHT = 76;
// Upper bound for the rendered shape width in pixels. Only takes effect
// when a template is extremely wide relative to its height (e.g. 3000×100),
// where the proportional scaling would produce an unreasonably wide rectangle.
const MAX_RENDERED_WIDTH = 150;

// =========================================================
// Ruler Layout
// =========================================================
//
// Each axis is laid out outward from the shape edge:
//
//   shape edge → [SHAPE_TO_RULER] → ruler line → [RULER_TO_LABEL] → label
//
// Tick marks extend TICK_HALF on each side of the ruler line, fitting
// within the two gaps. RULER_TOTAL is the sum of all three segments and
// determines how much space the rulers consume beyond the shape — the
// container is sized as shape + RULER_TOTAL on each axis, guaranteeing
// everything fits within overflow: hidden.

const SHAPE_TO_RULER = 4;
const RULER_TO_LABEL = 4;
const TICK_HALF = 2;
const LABEL_SIZE = 12;
const RULER_TOTAL = SHAPE_TO_RULER + RULER_TO_LABEL + LABEL_SIZE;

export default function DimensionIndicator({
  widthValue,
  heightValue,
  height = DEFAULT_HEIGHT,
}: DimensionIndicatorProps): React.JSX.Element {
  const theme = useTheme();
  const shapeBorderColor = theme.palette.secondary.main;
  const shapeFillColor = alpha(theme.palette.secondary.main, 0.2);
  const rulerColor = alpha(theme.palette.text.primary, 0.6);

  const { renderedWidth, renderedHeight } = useMemo(() => {
    const shapeHeight = height - RULER_TOTAL;
    const scale = shapeHeight / (heightValue / 10);

    return {
      renderedWidth: Math.min((widthValue / 10) * scale, MAX_RENDERED_WIDTH),
      renderedHeight: shapeHeight,
    };
  }, [widthValue, heightValue, height]);

  const containerWidth = renderedWidth + RULER_TOTAL;
  const containerHeight = height;

  const bottomRulerY = renderedHeight + SHAPE_TO_RULER;
  const bottomLabelY = bottomRulerY + RULER_TO_LABEL;
  const rightRulerX = renderedWidth + SHAPE_TO_RULER;
  const rightLabelX = rightRulerX + RULER_TO_LABEL;

  return (
    <div
      style={{
        display: 'inline-block',
        position: 'relative',
        width: containerWidth,
        height: containerHeight,
        overflow: 'hidden',
        fontFamily: 'Roboto, Arial, sans-serif',
      }}
    >
      {/* shape */}
      <div
        style={{
          width: renderedWidth,
          height: renderedHeight,
          border: `1.5px solid ${shapeBorderColor}`,
          borderRadius: '3px',
          backgroundColor: shapeFillColor,
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      />

      {/* ---- bottom width ruler ---- */}

      <div
        style={{
          position: 'absolute',
          top: bottomRulerY,
          left: 2,
          width: renderedWidth - 4,
          height: 1,
          backgroundColor: rulerColor,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: bottomRulerY - TICK_HALF,
          left: 2,
          width: 1,
          height: TICK_HALF * 2 + 1,
          backgroundColor: rulerColor,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: bottomRulerY - TICK_HALF,
          left: renderedWidth - 3,
          width: 1,
          height: TICK_HALF * 2 + 1,
          backgroundColor: rulerColor,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: bottomLabelY,
          left: 0,
          width: renderedWidth,
          textAlign: 'center',
          fontSize: '0.6rem',
          color: rulerColor,
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}
      >
        {widthValue}px
      </div>

      {/* ---- right height ruler ---- */}

      <div
        style={{
          position: 'absolute',
          left: rightRulerX,
          top: 2,
          width: 1,
          height: renderedHeight - 4,
          backgroundColor: rulerColor,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: rightRulerX - TICK_HALF,
          top: 2,
          width: TICK_HALF * 2 + 1,
          height: 1,
          backgroundColor: rulerColor,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: rightRulerX - TICK_HALF,
          top: renderedHeight - 3,
          width: TICK_HALF * 2 + 1,
          height: 1,
          backgroundColor: rulerColor,
        }}
      />
      {/* Uses writing-mode instead of transform: rotate so the layout box
          matches the visual box — this is what makes RULER_TOTAL correctly
          account for the label's horizontal footprint. */}
      <div
        style={{
          position: 'absolute',
          left: rightLabelX,
          top: 0,
          height: renderedHeight,
          display: 'flex',
          justifyContent: 'center',
          writingMode: 'vertical-rl',
          fontSize: '0.6rem',
          color: rulerColor,
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}
      >
        {heightValue}px
      </div>
    </div>
  );
}
